<template>
  <div class="apple-page">
    <header class="hero-section">
      <h1 class="hero-title">{{ t.title }}</h1>
      <p class="hero-subtitle">{{ t.desc }}</p>
    </header>

    <section class="map-section">
      <div class="glass-card map-container">
        <div ref="mapContainer" class="kakao-map"></div>
      </div>
    </section>

    <section class="content-section">
      <!-- Editor's Introduction Card -->
      <div class="glass-card intro-card mb-80">
        <div class="card-body">
          <span class="badge">Editor's Guide</span>
          <h2>동탄에서 아이와 함께하는 완벽한 주말</h2>
          <p>
            동탄 신도시는 '아이 키우기 좋은 도시'라는 명성답게 방대한 녹지 공간과 체계적인 놀이 시설을 갖추고 있습니다. 
            단순히 공원을 걷는 것 이상으로, 동탄의 각 공원들은 저마다의 특색을 가지고 있습니다. 
            예를 들어, <strong>동탄호수공원</strong>은 경관과 산책로가 일품이며, <strong>센트럴파크</strong>는 활동적인 스포츠와 바닥 분수로 유명합니다.
          </p>
          <p>
            무료로 이용 가능한 공공 시설부터, 프리미엄 서비스를 제공하는 유료 체험관까지 상황에 맞는 선택이 가능합니다. 
            주말 나들이를 계획하신다면, 오전에는 무료 공원에서 에너지를 발산하고, 오후에는 근처의 유료 체험관이나 키즈카페에서 창의적인 활동을 즐기는 코스를 추천드립니다.
          </p>
          <div class="tips-grid">
            <div class="tip-item">
              <span class="icon">🅿️</span>
              <p>주말 오후 공영주차장은 매우 혼잡합니다. 오전 11시 이전 방문을 권장합니다.</p>
            </div>
            <div class="tip-item">
              <span class="icon">🧺</span>
              <p>피크닉 시 지정된 장소 외 텐트 설치는 금지되어 있으니 돗자리를 준비하세요.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="section-header">
        <h2 class="section-title">{{ t.freeTitle }}</h2>
      </div>
      <div class="bento-grid">
        <div v-for="spot in freePlaces" :key="spot.name" class="apple-card clickable" @click="focusOnMap(spot)">
          <div class="card-content">
            <span class="card-tag free">{{ currentLang === 'ko' ? '무료' : 'Free' }}</span>
            <h3>{{ currentLang === 'ko' ? spot.name : spot.name_en }}</h3>
            <p class="address">{{ spot.address }}</p>
            <p class="price">{{ currentLang === 'ko' ? spot.price : spot.price_en }}</p>
            <p class="parking-info">🚗 {{ spot.parking }}</p>
            <button class="naver-btn" @click.stop="searchOnNaver(currentLang === 'ko' ? spot.name : spot.name_en)">Naver Search</button>
          </div>
        </div>
      </div>

      <div class="section-header mt-80">
        <h2 class="section-title">{{ t.paidTitle }}</h2>
      </div>
      <div class="bento-grid">
        <div v-for="spot in paidPlaces" :key="spot.name" class="apple-card clickable" @click="focusOnMap(spot)">
          <div class="card-content">
            <span class="card-tag paid">{{ currentLang === 'ko' ? '유료' : 'Paid' }}</span>
            <h3>{{ currentLang === 'ko' ? spot.name : spot.name_en }}</h3>
            <p class="address">{{ spot.address }}</p>
            <p class="price">{{ currentLang === 'ko' ? spot.price : spot.price_en }}</p>
            <p class="parking-info">🚗 {{ spot.parking }}</p>
            <button class="naver-btn" @click.stop="searchOnNaver(currentLang === 'ko' ? spot.name : spot.name_en)">Naver Search</button>
          </div>
        </div>
      </div>

      <!-- Bottom Ad Unit -->
      <AdComponent slotId="9173007135" />
    </section>
  </div>
</template>

<script>
/* global kakao */
import { ref, onMounted, computed, inject, onUnmounted, watch, nextTick } from 'vue';

import data from '../assets/data.json';

const TRANSLATIONS = {
  ko: {
    title: '아이와 뭐하지?',
    desc: '동탄 근교의 아이와 가기 좋은 장소들을 모았어요.',
    freeTitle: '무료 장소',
    paidTitle: '유료 장소'
  },
  en: {
    title: 'Where to go with Kids?',
    desc: 'Best places for kids around Dongtan.',
    freeTitle: 'Free Admission',
    paidTitle: 'Paid Experience'
  }
};

export default {
  name: 'KidsView',
  setup() {
    const currentLang = inject('currentLang', ref('ko'));
    const theme = inject('theme', ref('light'));
    const map = ref(null);
    const mapContainer = ref(null);
    const markers = ref([]);
    const t = computed(() => {
      const lang = currentLang.value || 'ko';
      const trans = TRANSLATIONS[lang] || TRANSLATIONS['ko'];
      return {
        title: trans.title || '',
        desc: trans.desc || '',
        freeTitle: trans.freeTitle || '',
        paidTitle: trans.paidTitle || ''
      };
    });

    const freePlaces = ref(data?.kids?.free || data?.free_places || []);
    const paidPlaces = ref(data?.kids?.paid || data?.paid_places || []);

    const initMap = () => {
      if (typeof kakao === 'undefined') {
        setTimeout(initMap, 500);
        return;
      }
      kakao.maps.load(() => {
        const container = mapContainer.value;
        if(!container) return;
        const options = { center: new kakao.maps.LatLng(37.197, 127.085), level: 7 };
        map.value = new kakao.maps.Map(container, options);

        // ✅ CSS 렌더링 완료 후 relayout → 마커 렌더
        nextTick(() => {
          setTimeout(() => {
            map.value.relayout();

            // 내 위치 가져오기
            if (navigator.geolocation) {
              navigator.geolocation.getCurrentPosition((position) => {
                const lat = position.coords.latitude;
                const lng = position.coords.longitude;
                const locPosition = new kakao.maps.LatLng(lat, lng);

                // 내 위치 마커 표시
                const imageSize = new kakao.maps.Size(24, 24);
                const markerImage = new kakao.maps.MarkerImage('https://t1.daumcdn.net/localimg/localimages/07/mapapidoc/markerStar.png', imageSize);
                new kakao.maps.Marker({
                  map: map.value,
                  position: locPosition,
                  image: markerImage,
                  title: "내 위치"
                });

                // 내 위치가 동탄 중심에서 너무 멀지 않을 때만 중심 이동
                const dongtanCenter = new kakao.maps.LatLng(37.197, 127.085);
                const line = new kakao.maps.Polyline({
                    path: [locPosition, dongtanCenter]
                });
                if (line.getLength() < 20000) { // 20km 이내일 때만
                    map.value.setCenter(locPosition);
                }
              });
            }

            renderMarkers();
          }, 300);
        });
      });
    };

    const renderMarkers = () => {
      if (!map.value) return;
      markers.value.forEach(m => {
        if (m.iw) m.iw.close();
        m.setMap(null);
      });
      markers.value = [];
      
      const bounds = new kakao.maps.LatLngBounds();
      const allItems = [...freePlaces.value, ...paidPlaces.value];
      
      allItems.forEach(spot => {
        const position = new kakao.maps.LatLng(spot.lat, spot.lng);
        const marker = new kakao.maps.Marker({
          position: position,
          map: map.value
        });
        
        const name = currentLang.value === 'ko' ? spot.name : spot.name_en;
        const iw = new kakao.maps.InfoWindow({ 
          content: `<div style="padding:10px;font-size:14px;color:#333;font-weight:600;min-width:150px;">${name}</div>`,
          removable: true
        });
        
        kakao.maps.event.addListener(marker, 'click', () => {
          markers.value.forEach(m => m.iw.close());
          iw.open(map.value, marker);
        });
        
        marker.iw = iw;
        marker.itemName = spot.name;
        markers.value.push(marker);
        bounds.extend(position);
      });

      if (allItems.length > 0) {
        map.value.setBounds(bounds);
      }
    };

    const focusOnMap = (item) => {
      if (!map.value) return;
      const position = new kakao.maps.LatLng(item.lat, item.lng);
      map.value.setCenter(position);
      map.value.setLevel(4);
      
      const marker = markers.value.find(m => m.itemName === item.name);
      if (marker) {
        markers.value.forEach(m => m.iw.close());
        marker.iw.open(map.value, marker);
      }
      
      window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    onMounted(async () => {
      await nextTick();
      setTimeout(initMap, 600);
      window.addEventListener('lang-changed', renderMarkers);
    });

    onUnmounted(() => {
      window.removeEventListener('lang-changed', renderMarkers);
    });

    watch(currentLang, () => renderMarkers());

    const searchOnNaver = (query) => {
      const url = `https://search.naver.com/search.naver?query=${encodeURIComponent(query)}`;
      window.open(url, '_blank');
    };

    return { currentLang, theme, t, freePlaces, paidPlaces, searchOnNaver, focusOnMap, mapContainer };
  }
}
</script>

<style scoped>
.apple-page { padding-bottom: 100px; }
.clickable { cursor: pointer; transition: opacity 0.3s; }
.clickable:hover { opacity: 0.7; text-decoration: underline; }
.hero-section { padding: 80px 22px 40px; text-align: center; max-width: 800px; margin: 0 auto; }
.hero-title { font-size: 3.5rem; font-weight: 700; letter-spacing: -0.015em; margin-bottom: 12px; }
.hero-subtitle { font-size: 1.5rem; color: var(--text-secondary); font-weight: 400; }
.map-section { max-width: 1024px; margin: 0 auto 80px; padding: 0 22px; }
.map-container { padding: 0 !important; overflow: hidden; }
.kakao-map { width: 100%; height: 500px; filter: grayscale(0.2); display: block; }
.content-section { max-width: 1024px; margin: 0 auto; padding: 0 22px; }
.section-header { margin-bottom: 30px; }
.section-title { font-size: 2rem; font-weight: 600; }
.mt-80 { margin-top: 80px; }
.bento-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 20px; }
.apple-card { background: var(--card-bg); border-radius: 22px; padding: 30px; transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1); display: flex; flex-direction: column; justify-content: flex-end; min-height: 200px; border: 1px solid transparent; }
.apple-card:hover { transform: scale(1.02); border: 1px solid var(--accent); }
.card-tag { font-size: 0.75rem; font-weight: 700; text-transform: uppercase; margin-bottom: 12px; display: block; }
.card-tag.free { color: var(--accent); }
.card-tag.paid { color: #ff3b30; }
.apple-card h3 { font-size: 1.4rem; font-weight: 600; margin: 0 0 8px 0; }
.address { font-size: 0.9rem; color: var(--text-secondary); margin-bottom: 4px; }
.price { font-size: 0.9rem; font-weight: 600; margin-bottom: 4px; }
.parking-info { font-size: 0.85rem; color: var(--accent); font-weight: 500; }
.naver-btn {
  margin-top: 12px;
  background: #03c75a;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
}
.naver-btn:hover { background: #02b351; }
@media (max-width: 734px) { .hero-title { font-size: 2.5rem; } .hero-subtitle { font-size: 1.2rem; } .bento-grid { grid-template-columns: 1fr; } }
</style>
